import {
  createUnifiedTheme,
  defaultComponentThemes,
  genPageTheme,
  palettes,
  shapes,
  UnifiedThemeProvider,
  type UnifiedTheme,
} from '@backstage/theme';
import { useState, useLayoutEffect, type PropsWithChildren } from 'react';

// Compiled from @bee/pattern-library's LESS color tokens by
// packages/app/scripts/build-eds-styles.js (see the root postinstall
// script). Gitignored/regenerated on every install rather than committed.
import './eds-tokens.generated.css';

import { resolveEdsColorTokens, type EdsColorTokens } from './tokens';

type EdsMode = 'light' | 'dark';

function buildEdsTheme(mode: EdsMode, tokens: EdsColorTokens) {
  const basePalette = mode === 'dark' ? palettes.dark : palettes.light;

  return createUnifiedTheme({
    palette: {
      ...basePalette,
      primary: { main: tokens.brandMain },
      secondary: { main: tokens.secondaryMain },
      error: { main: tokens.errorMain },
      warning: { main: tokens.warningMain },
      info: { main: tokens.brandMain },
      success: { main: tokens.successMain },
      background: {
        default: tokens.backgroundDefault,
        paper: tokens.backgroundPaper,
      },
      text: {
        primary: tokens.textPrimary,
        secondary: tokens.textSecondary,
      },
      divider: tokens.divider,
      link: tokens.brandMain,
      tabbar: {
        indicator: tokens.brandMain,
      },
      banner: {
        info: tokens.brandMain,
        error: tokens.errorMain,
        warning: tokens.warningMain,
        text: tokens.bannerText,
        link: tokens.bannerText,
      },
      errorBackground: tokens.errorMain,
      warningBackground: tokens.warningMain,
      infoBackground: tokens.brandMain,
      navigation: {
        background: tokens.backgroundPaper,
        submenu: { background: tokens.backgroundDefault },
        indicator: tokens.brandMain,
        selectedColor: tokens.brandMain,
        color: tokens.textSecondary,
        navItem: { hoverBackground: tokens.divider },
      },
    },
    pageTheme: {
      home: genPageTheme({
        colors: [tokens.brandMain, tokens.brandDark],
        shape: shapes.wave,
        options: { fontColor: tokens.bannerText },
      }),
      documentation: genPageTheme({
        colors: [tokens.brandMain, tokens.brandDark],
        shape: shapes.wave,
        options: { fontColor: tokens.bannerText },
      }),
      project: genPageTheme({
        colors: [tokens.brandDark, tokens.brandMain],
        shape: shapes.wave,
        options: { fontColor: tokens.bannerText },
      }),
      tool: genPageTheme({
        colors: [tokens.brandMain, tokens.brandDark],
        shape: shapes.round,
        options: { fontColor: tokens.bannerText },
      }),
      library: genPageTheme({
        colors: [tokens.brandMain, tokens.brandDark],
        shape: shapes.round,
        options: { fontColor: tokens.bannerText },
      }),
      technique: genPageTheme({
        colors: [tokens.brandMain, tokens.brandDark],
        shape: shapes.round,
        options: { fontColor: tokens.bannerText },
      }),
      other: genPageTheme({
        colors: [tokens.brandMain, tokens.brandDark],
        shape: shapes.wave,
        options: { fontColor: tokens.bannerText },
      }),
      apis: genPageTheme({
        colors: [tokens.brandMain, tokens.brandDark],
        shape: shapes.wave,
        options: { fontColor: tokens.bannerText },
      }),
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: theme => {
          const baseFn = defaultComponentThemes?.MuiCssBaseline?.styleOverrides;
          const baseStyles = typeof baseFn === 'function' ? baseFn(theme) : {};
          return typeof baseStyles === 'object' && baseStyles ? baseStyles : {};
        },
      },
      MuiButton: {
        styleOverrides: {
          ...defaultComponentThemes?.MuiButton?.styleOverrides,
          root: {
            textTransform: 'none',
          },
        },
      },
    },
  });
}

/**
 * Applies the html.theme-{light,dark} class BEE's compiled stylesheet keys
 * off of, then constructs the UnifiedTheme from the now-resolvable CSS
 * custom properties.
 *
 * The class is applied in useLayoutEffect (before paint), but resolving and
 * building the theme must happen *after* that DOM mutation too, in the same
 * effect — resolving during render (e.g. via useMemo) would read whatever
 * class happened to be on <html> from a previous render or a previous
 * theme's cleanup, not the class this effect is about to set. Building the
 * theme in the layout effect and pushing it into state instead re-renders
 * synchronously before the browser paints, so there's still no flash of
 * unthemed/mis-themed content, just resolved against the *correct* class.
 * The initial state is a same-mode guess (best-effort, may read stale DOM
 * state) purely so first render has something to pass to UnifiedThemeProvider.
 */
function EdsThemeProvider({ mode, children }: PropsWithChildren<{ mode: EdsMode }>) {
  const [theme, setTheme] = useState<UnifiedTheme>(() =>
    buildEdsTheme(mode, resolveEdsColorTokens(mode)),
  );

  useLayoutEffect(() => {
    const { classList } = document.documentElement;
    const otherClass = mode === 'light' ? 'theme-dark' : 'theme-light';
    const ownClass = `theme-${mode}`;
    classList.remove(otherClass);
    classList.add(ownClass);
    setTheme(buildEdsTheme(mode, resolveEdsColorTokens(mode)));
    return () => classList.remove(ownClass);
  }, [mode]);

  return (
    <UnifiedThemeProvider theme={theme} themeName={`eds-${mode}`}>
      {children}
    </UnifiedThemeProvider>
  );
}

export function EdsLightThemeProvider({ children }: PropsWithChildren<{}>) {
  return <EdsThemeProvider mode="light">{children}</EdsThemeProvider>;
}

export function EdsDarkThemeProvider({ children }: PropsWithChildren<{}>) {
  return <EdsThemeProvider mode="dark">{children}</EdsThemeProvider>;
}
